import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";

/**
 * Shared control flow for the list-CRUD screens (parties/assets/loans/
 * capgains/reminders/accounts/categories): load-on-mount into `items`,
 * surface load failures into `error`, and reload after every successful
 * create/update/remove.
 *
 * create/update/remove deliberately RE-THROW on failure: the screens decide
 * where a failure surfaces (usually the modal's own formError for save, the
 * screen-level error for delete), so the hook never swallows anything.
 * `setError` is exposed so those action errors land in the same error slot
 * this hook renders load failures into.
 */
export interface CrudResource<T extends { id: string }> {
  items: T[];
  loading: boolean;
  error: string | null;
  setError: (msg: string | null) => void;
  reload: () => void;
  create: (body: unknown) => Promise<void>;
  update: (id: string, body: unknown) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useCrudResource<T extends { id: string }>(
  endpoint: string,
  /** Overrides the auto-derived resource name in the network-error fallback
   * message (e.g. "/api/capgains" would otherwise read "failed to load
   * capgains" — CapGains passes "capital gains" instead). */
  fallbackName?: string
): CrudResource<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    // "/api/capgains" -> "capgains" for the network-error fallback message.
    const name = fallbackName ?? endpoint.replace(/^\/api\//, "").replace(/[-_]/g, " ");
    api
      .get<T[]>(endpoint)
      .then(setItems)
      .catch((err) => setError(err instanceof ApiError ? err.message : `failed to load ${name}`))
      .finally(() => setLoading(false));
  }, [endpoint, fallbackName]);

  useEffect(reload, [reload]);

  const create = useCallback(
    async (body: unknown) => {
      await api.post(endpoint, body);
      reload();
    },
    [endpoint, reload]
  );

  const update = useCallback(
    async (id: string, body: unknown) => {
      await api.patch(`${endpoint}/${id}`, body);
      reload();
    },
    [endpoint, reload]
  );

  const remove = useCallback(
    async (id: string) => {
      await api.delete(`${endpoint}/${id}`);
      reload();
    },
    [endpoint, reload]
  );

  return { items, loading, error, setError, reload, create, update, remove };
}
